/**
 * 供应商流式测试的请求与 SSE 解析。
 * 请求方式与旧版一致：fetch POST 到 test-stream 接口，Bearer token，body 为 { test_message }；
 * 后端逐行发送 `data: {...}`，事件类型有 start / ttfb / content / usage / complete / error。
 */
import { API_ENDPOINTS } from '@/api/endpoints'

export type StreamEvent =
  | { type: 'start'; provider?: string; model?: string }
  | { type: 'ttfb'; time: number }
  | { type: 'content'; content: string }
  | { type: 'usage'; usage: unknown }
  | {
      type: 'complete'
      metrics: { ttfb: number; total_time: number; total_chars: number; chars_per_sec: number }
    }
  | { type: 'error'; error: string }

// 解析一行 SSE；不是 data 行或 JSON 不合法时返回 null
export function parseSseLine(line: string): StreamEvent | null {
  if (!line.startsWith('data: ')) return null
  try {
    return JSON.parse(line.slice(6)) as StreamEvent
  } catch (error) {
    console.error('解析SSE数据失败:', error)
    return null
  }
}

// 逐块读取响应流，按行回调事件；一行被拆在两个块里时先缓存，等下一块拼完整再解析
export async function readSseStream(
  reader: ReadableStreamDefaultReader<Uint8Array>,
  onEvent: (event: StreamEvent) => void
): Promise<void> {
  const decoder = new TextDecoder()
  let buffer = ''
  for (;;) {
    const { done, value } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true })
    const lines = buffer.split('\n')
    buffer = lines.pop() ?? ''
    for (const line of lines) {
      const event = parseSseLine(line)
      if (event) onEvent(event)
    }
  }
  const lastEvent = parseSseLine(buffer + decoder.decode())
  if (lastEvent) onEvent(lastEvent)
}

// 发起流式测试请求，返回响应流的 reader
export async function openTestStream(
  providerId: number,
  testMessage: string,
  token: string | null,
  signal?: AbortSignal
): Promise<ReadableStreamDefaultReader<Uint8Array>> {
  const baseUrl = import.meta.env.VITE_API_BASE_URL || ''
  const response = await fetch(`${baseUrl}${API_ENDPOINTS.aiProviders.testStream(providerId)}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token ?? ''}`
    },
    body: JSON.stringify({ test_message: testMessage }),
    signal
  })
  if (!response.ok) throw new Error(`HTTP ${response.status}`)
  const reader = response.body?.getReader()
  if (!reader) throw new Error('无法获取响应流')
  return reader
}
